import axios from 'axios';

// Use relative URL so API calls go to same origin in both preview and production
const API = '/api';

axios.defaults.withCredentials = true;

// Auto-refresh token on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

const HTTP_UNAUTHORIZED = 401;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === HTTP_UNAUTHORIZED && !originalRequest._retry && !originalRequest.url?.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Profiles API
export const profilesApi = {
  create: (data) => axios.post(`${API}/profiles`, data),
  getAll: () => axios.get(`${API}/profiles`),
  getById: (id, invite = null) => axios.get(`${API}/profiles/${id}${invite ? `?invite=${invite}` : ''}`),
  update: (id, data) => axios.put(`${API}/profiles/${id}`, data),
  delete: (id) => axios.delete(`${API}/profiles/${id}`),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/profiles/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  setStockPhoto: (id, stockUrl) => axios.post(`${API}/profiles/${id}/stock-photo`, { stock_url: stockUrl })
};

// Story Sections API
export const sectionsApi = {
  create: (profileId, data) => axios.post(`${API}/profiles/${profileId}/sections`, data),
  getAll: (profileId, invite = null) => axios.get(`${API}/profiles/${profileId}/sections${invite ? `?invite=${invite}` : ''}`),
  update: (profileId, sectionId, data) => axios.put(`${API}/profiles/${profileId}/sections/${sectionId}`, data),
  delete: (profileId, sectionId) => axios.delete(`${API}/profiles/${profileId}/sections/${sectionId}`)
};

// Contributions API
export const contributionsApi = {
  create: (profileId, data) => axios.post(`${API}/profiles/${profileId}/contributions`, data),
  getAll: (profileId, invite = null) => axios.get(`${API}/profiles/${profileId}/contributions${invite ? `?invite=${invite}` : ''}`),
  update: (profileId, contribId, data) => axios.put(`${API}/profiles/${profileId}/contributions/${contribId}`, data),
  delete: (profileId, contribId) => axios.delete(`${API}/profiles/${profileId}/contributions/${contribId}`)
};

// Gallery API
export const galleryApi = {
  upload: (profileId, file, caption = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', caption);
    return axios.post(`${API}/profiles/${profileId}/gallery?caption=${encodeURIComponent(caption)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAll: (profileId, invite = null) => axios.get(`${API}/profiles/${profileId}/gallery${invite ? `?invite=${invite}` : ''}`),
  delete: (profileId, imageId) => axios.delete(`${API}/profiles/${profileId}/gallery/${imageId}`)
};

// Extols API
export const extolsApi = {
  create: (profileId, data, invite = null) => axios.post(`${API}/profiles/${profileId}/extols${invite ? `?invite=${invite}` : ''}`, data),
  getAll: (profileId, invite = null) => axios.get(`${API}/profiles/${profileId}/extols${invite ? `?invite=${invite}` : ''}`),
  delete: (profileId, extolId) => axios.delete(`${API}/profiles/${profileId}/extols/${extolId}`),
  react: (profileId, extolId, reactionType) => axios.post(`${API}/profiles/${profileId}/extols/${extolId}/react`, { reaction_type: reactionType })
};

// Invites API
export const invitesApi = {
  create: (profileId, data = {}) => axios.post(`${API}/profiles/${profileId}/invites`, data),
  getAll: (profileId) => axios.get(`${API}/profiles/${profileId}/invites`),
  delete: (profileId, inviteId) => axios.delete(`${API}/profiles/${profileId}/invites/${inviteId}`)
};

// Explore API
export const exploreApi = {
  search: (params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.country) query.append('country', params.country);
    if (params.category) query.append('category', params.category);
    if (params.profile_type) query.append('profile_type', params.profile_type);
    if (params.page) query.append('page', params.page);
    return axios.get(`${API}/explore?${query.toString()}`);
  }
};

// Favourites API
export const favouritesApi = {
  toggle: (profileId) => axios.post(`${API}/profiles/${profileId}/favourite`),
  getAll: () => axios.get(`${API}/favourites`)
};

// Notifications API
export const notificationsApi = {
  getAll: () => axios.get(`${API}/notifications`),
  getUnreadCount: () => axios.get(`${API}/notifications/unread-count`),
  markRead: () => axios.post(`${API}/notifications/mark-read`)
};

// Admin API
export const adminApi = {
  getStats: () => axios.get(`${API}/admin/stats`),
  getUsers: (page = 1) => axios.get(`${API}/admin/users?page=${page}`),
  blockUser: (userId) => axios.post(`${API}/admin/users/${userId}/block`),
  setTier: (userId, tier) => axios.post(`${API}/admin/users/${userId}/tier?tier=${tier}`),
  getProfiles: (status = null, page = 1) => axios.get(`${API}/admin/profiles?page=${page}${status ? `&status=${status}` : ''}`),
  setProfileStatus: (profileId, status) => axios.post(`${API}/admin/profiles/${profileId}/status?status=${status}`),
  getReports: (status = null) => axios.get(`${API}/admin/reports${status ? `?status=${status}` : ''}`),
  resolveReport: (reportId, action) => axios.post(`${API}/admin/reports/${reportId}/resolve?action=${action}`),
  getInbox: (status = null) => axios.get(`${API}/admin/inbox${status ? `?status=${status}` : ''}`),
  markRead: (messageId) => axios.post(`${API}/admin/inbox/${messageId}/read`),
  resolveMessage: (messageId) => axios.post(`${API}/admin/inbox/${messageId}/resolve`)
};

// Contact API
export const contactApi = {
  send: (data) => axios.post(`${API}/contact`, data)
};

// Reports API
export const reportsApi = {
  create: (profileId, data) => axios.post(`${API}/profiles/${profileId}/report`, data)
};

// Pending Contributions API
export const pendingContributionsApi = {
  create: (profileId, data, invite = null) => axios.post(`${API}/profiles/${profileId}/pending-contributions${invite ? `?invite=${invite}` : ''}`, data),
  getAll: (profileId) => axios.get(`${API}/profiles/${profileId}/pending-contributions`),
  approve: (profileId, contribId) => axios.post(`${API}/profiles/${profileId}/pending-contributions/${contribId}/approve`),
  reject: (profileId, contribId) => axios.post(`${API}/profiles/${profileId}/pending-contributions/${contribId}/reject`)
};

// Send Tribute API
export const sendTributeApi = {
  send: (profileId, data) => axios.post(`${API}/profiles/${profileId}/send`, data)
};

// Warm Words API
export const warmWordsApi = {
  create: (profileId, data) => axios.post(`${API}/profiles/${profileId}/warm-words`, data),
  getAll: (profileId) => axios.get(`${API}/profiles/${profileId}/warm-words`)
};

// Auth API (for forgot/reset password)
export const authApi = {
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, newPassword) => axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword })
};

export default { profilesApi, sectionsApi, contributionsApi, galleryApi, extolsApi, invitesApi, exploreApi, favouritesApi, notificationsApi, adminApi, authApi };
